import { get_currencies } from '@app/pages/currrencies/redux/reducer';
import { getCurrencies } from '@app/pages/currrencies/redux/selectors';
import { get_meals, get_export } from '@app/pages/meals/redux/reducer';
import { getPageStats, getExportData } from '@app/pages/meals/redux/selector';
import { FieldArray, Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { IoMdAddCircleOutline, IoMdClose } from 'react-icons/io';
import { MdDeleteOutline, MdCloudUpload } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { creatingRecipesGroups } from '../redux/selectors';
import { create_recipe_group, update_recipe_group } from '../redux/reducer';
import toast from 'react-hot-toast';

const mealTypeOptions = [
  { value: 'Breakfast', label: 'Breakfast' },
  { value: 'Lunch', label: 'Lunch' },
  { value: 'Dinner', label: 'Dinner' }
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '48px',
    borderRadius: '10px',
    borderColor: state.isFocused ? '#549A9A' : '#E7E7E9',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(84, 154, 154, 0.2)' : 'none',
    '&:hover': { borderColor: '#549A9A' }
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#F0F7F7',
    borderRadius: '6px'
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#003333',
    fontWeight: 500
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#003333',
    '&:hover': { backgroundColor: '#ACD1D1', color: '#003333' }
  })
};

const FormField = ({ label, required, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium text-[#14151A]">
      {label}
      {required && <span className="text-[#E6483D] ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputClass =
  'h-12 border border-[#E7E7E9] rounded-[10px] px-3 text-sm font-normal focus:outline-none focus:border-[#549A9A] focus:ring-2 focus:ring-[#549A9A]/20 transition-all placeholder:text-[#B0B3BD]';

const CreateRecipeGroup = ({ isOpen, onClose, content, isEdit }) => {
  const dispatch = useDispatch();
  const exportData = useSelector(getExportData);
  const pageStats = useSelector(getPageStats);
  const [image, setImage] = useState(null);
  const imageRef = useRef(null);
  const currencies = useSelector(getCurrencies);
  const creatingGroup = useSelector(creatingRecipesGroups);

  const [form, setForm] = useState({
    name: '',
    description: '',
    price: {
      currency: '',
      value: 0
    },
    servings: 0,
    plan: '',
    benefits: [''],
    meal_types: [],
    recipes: []
  });

  useEffect(() => {
    if (content !== null && isEdit) {
      setForm({
        name: content?.name,
        description: content?.description,
        servings: content?.servings,
        price: {
          currency: content?.price?.currency?._id,
          value: content?.price?.value
        },
        plan: content?.plan,
        benefits: content?.benefits?.length === 0 ? [''] : content?.benefits,
        meal_types: content?.meal_types?.map((item) => ({
          value: item,
          label: item
        })),
        recipes: content?.recipes?.map((item) => ({
          value: item?._id,
          label: item?.name
        }))
      });
      setImage(content?.image);
    }
  }, [content, isEdit]);

  useEffect(() => {
    dispatch(get_meals({ page: 1, limit: 10 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(get_currencies());
  }, [dispatch]);

  useEffect(() => {
    if (pageStats?.docs) {
      dispatch(get_export({ page: 1, limit: pageStats?.docs }));
    }
  }, [dispatch, pageStats]);

  const recipesData = exportData?.map((item) => ({
    value: item?._id,
    label: item.name
  }));

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
    }
  };

  const handleImageClick = () => {
    imageRef.current?.click();
  };

  const handleSubmit = (doc) => {
    if (image === null) {
      return toast.error('Add Image!!');
    }

    const recipes = doc.recipes.map((item) => item.value);
    const meal_types = doc.meal_types.map((item) => item.value);
    let data = {
      ...doc,
      price: {
        currency: doc.price.currency,
        value: Number(doc.price.value)
      },
      benefits: doc.benefits.filter((item) => item.trim() !== ''),
      servings: Number(doc.servings),
      image: image,
      recipes: recipes,
      meal_types: meal_types
    };

    if (isEdit) {
      dispatch(update_recipe_group({ data, id: content?._id }));
    } else {
      dispatch(create_recipe_group(data));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="flex fixed inset-0 z-50 justify-end">
      <div
        className="fixed inset-0 backdrop-blur-[4px] bg-black/20"
        onClick={onClose}
      />

      <div className="z-20 relative bg-white flex flex-col w-full max-w-[560px] h-full shadow-[-4px_0px_24px_0px_rgba(0,0,0,0.08)] animate-slide-in">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-5 border-b border-[#E7E7E9]">
          <div>
            <h2 className="text-xl font-semibold text-[#14151A]">
              {isEdit ? 'Edit Recipe Group' : 'New Recipe Group'}
            </h2>
            <p className="text-sm text-[#7C8091] mt-0.5">
              {isEdit
                ? 'Update the details of this recipe group'
                : 'Fill in the details to create a new recipe group'}
            </p>
          </div>
          <button
            id="close"
            className="size-9 border border-[#E7E7E9] rounded-[8px] grid place-items-center cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={onClose}
          >
            <IoMdClose color="#666666" size={20} />
          </button>
        </div>

        {/* Scrollable form content */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          <Formik enableReinitialize initialValues={form} onSubmit={handleSubmit}>
            {({ values, handleChange, setFieldValue }) => (
              <Form className="p-6 space-y-5">
                {/* Image Upload */}
                <div>
                  <input
                    className="hidden"
                    type="file"
                    accept=".jpg, .png, .jpeg"
                    onChange={handleImage}
                    ref={imageRef}
                  />
                  {image === null ? (
                    <div
                      className="border-2 border-dashed border-[#E7E7E9] rounded-[12px] p-8 cursor-pointer hover:border-[#549A9A] hover:bg-[#F0F7F7]/30 transition-all group"
                      onClick={handleImageClick}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="size-12 rounded-full bg-[#F0F7F7] grid place-items-center mb-3 group-hover:bg-[#ACD1D1]/30 transition-colors">
                          <MdCloudUpload size={24} className="text-[#549A9A]" />
                        </div>
                        <p className="text-sm font-medium text-[#14151A]">
                          Click to upload image
                        </p>
                        <p className="text-xs text-[#7C8091] mt-1">
                          PNG, JPG or JPEG (max 5MB)
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="relative rounded-[12px] overflow-hidden cursor-pointer group"
                      onClick={handleImageClick}
                    >
                      <img
                        src={
                          typeof image === 'string'
                            ? image
                            : URL.createObjectURL(image)
                        }
                        alt="preview"
                        className="w-full h-[180px] object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <p className="text-white text-sm font-medium">
                          Click to change image
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Name */}
                <FormField label="Name" required>
                  <input
                    value={values.name}
                    onChange={handleChange}
                    placeholder="Enter recipe group name"
                    required
                    name="name"
                    className={`${inputClass} capitalize`}
                  />
                </FormField>

                {/* Description */}
                <FormField label="Description">
                  <textarea
                    value={values.description}
                    onChange={handleChange}
                    placeholder="Describe this recipe group..."
                    rows={3}
                    name="description"
                    className="border border-[#E7E7E9] rounded-[10px] px-3 py-3 text-sm font-normal focus:outline-none focus:border-[#549A9A] focus:ring-2 focus:ring-[#549A9A]/20 transition-all placeholder:text-[#B0B3BD] resize-none"
                  />
                </FormField>

                {/* Currency & Price row */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Currency" required>
                    <select
                      value={values.price.currency}
                      onChange={(e) => {
                        setFieldValue('price.currency', e.target.value);
                      }}
                      required
                      className={`${inputClass} capitalize`}
                    >
                      <option value="">Select currency</option>
                      {currencies?.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.symbol} - {item.short_code}
                        </option>
                      ))}
                    </select>
                  </FormField>

                  <FormField label="Price" required>
                    <input
                      value={values.price.value}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setFieldValue('price.value', value);
                      }}
                      placeholder="0.00"
                      required
                      className={inputClass}
                    />
                  </FormField>
                </div>

                {/* Servings & Plan row */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Servings" required>
                    <input
                      value={values.servings}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setFieldValue('servings', value);
                      }}
                      placeholder="e.g. 4"
                      required
                      className={inputClass}
                    />
                  </FormField>

                  <FormField label="Plan" required>
                    <input
                      value={values.plan}
                      type="text"
                      onChange={handleChange}
                      placeholder="e.g. 7 days"
                      required
                      name="plan"
                      className={inputClass}
                    />
                  </FormField>
                </div>

                {/* Meal Types */}
                <FormField label="Meal Types" required>
                  <Select
                    name="meal_types"
                    value={values.meal_types}
                    options={mealTypeOptions}
                    isMulti
                    placeholder="Select meal types..."
                    styles={selectStyles}
                    onChange={(value) => {
                      setFieldValue('meal_types', value);
                    }}
                  />
                </FormField>

                {/* Recipes */}
                <FormField label="Recipes" required>
                  <Select
                    name="recipes"
                    isDisabled={recipesData?.length === 0}
                    options={recipesData}
                    value={values.recipes}
                    isSearchable
                    isMulti
                    placeholder="Search and select recipes..."
                    styles={selectStyles}
                    onChange={(value) => {
                      setFieldValue('recipes', value);
                    }}
                  />
                </FormField>

                {/* Benefits */}
                <FormField label="Benefits" required>
                  <FieldArray name="benefits">
                    {({ push, remove }) => (
                      <div className="space-y-2.5">
                        {values.benefits &&
                          values.benefits.map((item, idx) => (
                            <div key={idx} className="flex gap-2 items-center">
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => {
                                  setFieldValue(`benefits.${idx}`, e.target.value);
                                }}
                                placeholder="e.g. Flexibility: Swap ingredients to match preferences"
                                className={`${inputClass} w-full`}
                              />
                              {values.benefits.length > 1 && (
                                <button
                                  onClick={() => remove(idx)}
                                  type="button"
                                  className="size-9 flex-shrink-0 grid place-items-center rounded-[8px] border border-[#E7E7E9] hover:bg-red-50 hover:border-red-200 transition-colors"
                                >
                                  <MdDeleteOutline
                                    size={18}
                                    className="text-[#7C8091] hover:text-[#E6483D]"
                                  />
                                </button>
                              )}
                            </div>
                          ))}
                        <button
                          onClick={() => push('')}
                          type="button"
                          className="flex items-center gap-1.5 text-sm font-medium text-[#549A9A] hover:text-[#003333] transition-colors"
                        >
                          <IoMdAddCircleOutline size={18} />
                          Add benefit
                        </button>
                      </div>
                    )}
                  </FieldArray>
                </FormField>

                {/* Submit */}
                <div className="pt-2 pb-4">
                  <button
                    disabled={creatingGroup}
                    className="w-full rounded-[10px] bg-[#549A9A] hover:bg-[#478585] text-white py-3 text-sm font-semibold disabled:opacity-45 transition-colors"
                    type="submit"
                  >
                    {creatingGroup
                      ? 'Submitting...'
                      : isEdit
                      ? 'Save Changes'
                      : 'Create Recipe Group'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default CreateRecipeGroup;
